import type { BillingInterval, CheckoutTier } from "@/lib/checkout-cookie";

/**
 * Catalog amounts as the backend serves them (GET /api/saas/pricing for the
 * marketing site, POST /saas-auth/checkout/context for the checkout step):
 * the Paddle prices behind PADDLE_PRICE_*, amounts in minor units. The one
 * source of truth for every euro figure on this site — the static copy in
 * constants/tier.tsx is only the offline fallback.
 */
export interface CatalogPrice {
  priceId: string;
  tier: CheckoutTier;
  interval: BillingInterval;
  /** Minor units (1900 = 19,00 €). */
  amount: number;
  currency: string;
  trialDays: number | null;
  /** Max seats on that price (Solo = 1). */
  quantityMax?: number | null;
}

export type CatalogPricing = Partial<Record<CheckoutTier, Partial<Record<BillingInterval, CatalogPrice>>>>;

export const isTier = (v: unknown): v is CheckoutTier => v === "solo" || v === "team";
export const isInterval = (v: unknown): v is BillingInterval => v === "monthly" || v === "yearly";

export function parsePricing(raw: unknown): CatalogPricing | null {
  if (!raw || typeof raw !== "object") return null;
  const out: CatalogPricing = {};
  for (const tier of ["solo", "team"] as const) {
    const block = (raw as Record<string, unknown>)[tier];
    if (!block || typeof block !== "object") continue;
    for (const interval of ["monthly", "yearly"] as const) {
      const p = (block as Record<string, unknown>)[interval] as Partial<CatalogPrice> | undefined;
      if (!p || typeof p !== "object" || typeof p.priceId !== "string" || typeof p.amount !== "number") continue;
      (out[tier] ??= {})[interval] = {
        priceId: p.priceId,
        tier,
        interval,
        amount: p.amount,
        currency: typeof p.currency === "string" ? p.currency : "EUR",
        trialDays: typeof p.trialDays === "number" ? p.trialDays : null,
        quantityMax: typeof p.quantityMax === "number" ? p.quantityMax : null,
      };
    }
  }
  return out;
}

export function hasAnyPrice(pricing: CatalogPricing | null | undefined): pricing is CatalogPricing {
  return !!pricing && (["solo", "team"] as const).some((t) => Object.keys(pricing[t] ?? {}).length > 0);
}
